import { useCallback, useEffect, useState } from "react";
import "./bop030w030.css";
import BOP030W030Template from "./bop030w030.template";
import {
  EXPENSE_TYPE,
  normalizeCategoryLarge,
  normalizeType,
  type Category,
} from "../../common/commonConstant";
import useApiRequest from "../../common/useApiRequest";

export interface RegularIncomeExpenseItem {
  serialNo: string;
  type: string;
  categoryLarge: string;
  categorySmall: string;
  amount: number;
  memo: string;
  keihiFlg: number;
  mikakuteiFlg: number;
}

export interface RegularFormValues {
  serialNo: string | null; // nullなら新規追加
  type: Category;
  categoryLarge: string;
  categorySmall: string;
  amount: string;
  memo: string;
  keihiFlg: 0 | 1;
  mikakuteiFlg: 0 | 1;
}

const isRegularIncomeExpenseItem = (value: unknown): value is RegularIncomeExpenseItem => {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.serialNo === "string" &&
    typeof v.type === "string" &&
    typeof v.categoryLarge === "string" &&
    typeof v.categorySmall === "string" &&
    typeof v.amount === "number" &&
    typeof v.keihiFlg === "number" &&
    typeof v.mikakuteiFlg === "number"
  );
};

const emptyForm: RegularFormValues = {
  serialNo: null,
  type: EXPENSE_TYPE,
  categoryLarge: "",
  categorySmall: "",
  amount: "",
  memo: "",
  keihiFlg: 0,
  mikakuteiFlg: 0,
};

/**
 * 機能ID: BOP030W030
 * 機能名: 定期収支管理画面（regular_income_expenseのCRUD）
 * 毎月月初にバッチ（bop020b010）が本画面の登録内容をもとにその月の収支を自動登録する。
 */
const BOP030W030 = () => {
  const [items, setItems] = useState<RegularIncomeExpenseItem[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formValues, setFormValues] = useState<RegularFormValues>(emptyForm);
  const { isLoading: isListLoading, execute: fetchList } =
    useApiRequest<RegularIncomeExpenseItem[]>();
  const { isLoading: isSubmitting, execute } = useApiRequest();

  const loadList = useCallback(async () => {
    const result = await fetchList(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs04001`,
      { method: "GET" },
    );
    if (!result.ok) {
      alert("定期収支の一覧取得に失敗しました。画面を再読み込みしてご確認ください。");
      return;
    }
    const data = result.data;
    if (data !== undefined && (!Array.isArray(data) || !data.every(isRegularIncomeExpenseItem))) {
      alert("定期収支の一覧取得に失敗しました。画面を再読み込みしてご確認ください。");
      return;
    }
    setItems(data ?? []);
  }, [fetchList]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleOpenAddForm = () => {
    setFormValues(emptyForm);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (item: RegularIncomeExpenseItem) => {
    setFormValues({
      serialNo: item.serialNo,
      type: normalizeType(item.type),
      categoryLarge: normalizeCategoryLarge(item.categoryLarge || ""),
      categorySmall: item.categorySmall || "",
      amount: String(item.amount),
      memo: item.memo || "",
      keihiFlg: item.keihiFlg === 1 ? 1 : 0,
      mikakuteiFlg: item.mikakuteiFlg === 1 ? 1 : 0,
    });
    setIsFormOpen(true);
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setFormValues(emptyForm);
  };

  const handleFormTypeChange = (type: Category) => {
    setFormValues((prev) => ({
      ...prev,
      type,
      categoryLarge: "",
      categorySmall: "",
    }));
  };

  const handleFormCategoryLargeChange = (value: string) => {
    setFormValues((prev) => ({ ...prev, categoryLarge: value, categorySmall: "" }));
  };

  const handleFormSubmit = async () => {
    const missingFields: string[] = [];
    if (!formValues.type) missingFields.push("収支");
    if (!formValues.categoryLarge) missingFields.push("項目（大）");
    if (!formValues.categorySmall) missingFields.push("項目（小）");
    if (!formValues.amount.trim()) missingFields.push("金額（円）");

    if (missingFields.length > 0) {
      alert(`必須項目を入力してください: ${missingFields.join("、")}`);
      return;
    }

    const amount = Number(formValues.amount);
    if (!Number.isFinite(amount) || !Number.isInteger(amount) || amount < 0) {
      alert("金額（円）は0以上の整数で入力してください");
      return;
    }

    const isEditing = formValues.serialNo !== null;
    const url = isEditing
      ? `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs04003`
      : `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs04002`;

    const payload = {
      ...(isEditing ? { serialNo: formValues.serialNo } : {}),
      type: formValues.type,
      categoryLarge: formValues.categoryLarge,
      categorySmall: formValues.categorySmall,
      amount,
      memo: formValues.memo,
      keihiFlg: formValues.keihiFlg,
      mikakuteiFlg: formValues.mikakuteiFlg,
    };

    const result = await execute(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (!result.ok) {
      alert(result.error.message);
      return;
    }

    alert(isEditing ? "更新成功" : "登録成功");
    setIsFormOpen(false);
    setFormValues(emptyForm);
    await loadList();
  };

  const handleDelete = async (item: RegularIncomeExpenseItem) => {
    const confirmed = window.confirm(
      `「${item.categoryLarge} / ${item.categorySmall}（${item.amount}円）」を削除してもよろしいですか？`,
    );
    if (!confirmed) return;

    const result = await execute(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs04004`,
      {
        method: "POST",
        body: JSON.stringify({ serialNo: item.serialNo }),
      },
    );

    if (!result.ok) {
      alert(result.error.message);
      return;
    }

    alert("削除成功");
    await loadList();
  };

  return (
    <BOP030W030Template
      items={items}
      isFormOpen={isFormOpen}
      formValues={formValues}
      isLoading={isListLoading || isSubmitting}
      onOpenAddForm={handleOpenAddForm}
      onOpenEditForm={handleOpenEditForm}
      onCancelForm={handleCancelForm}
      onFormTypeChange={handleFormTypeChange}
      onFormCategoryLargeChange={handleFormCategoryLargeChange}
      onFormCategorySmallChange={(value) =>
        setFormValues((prev) => ({ ...prev, categorySmall: value }))
      }
      onFormAmountChange={(value) => setFormValues((prev) => ({ ...prev, amount: value }))}
      onFormMemoChange={(value) => setFormValues((prev) => ({ ...prev, memo: value }))}
      onFormKeihiFlgChange={(checked) =>
        setFormValues((prev) => ({ ...prev, keihiFlg: checked ? 1 : 0 }))
      }
      onFormMikakuteiFlgChange={(checked) =>
        setFormValues((prev) => ({ ...prev, mikakuteiFlg: checked ? 1 : 0 }))
      }
      onFormSubmit={handleFormSubmit}
      onDelete={handleDelete}
    />
  );
};

export default BOP030W030;
